import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { theme, media, pxToCssFont, pxToRem } from '@vfit/shared/themes';
import { PriceVariant, PriceVariantComponent, PriceVariantForStyled } from '@vfit/shared/models';

// sunrise theme
const common = {
  amount: css`
    ${pxToCssFont(30, 38)}
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.extrabold};

    ${media.desktop`
      font-size: ${theme.typography.fontSize.h1};
      line-height: ${theme.typography.lineHeight.h1};
    `}
  `,

  label: css`
    margin: 0;
    font-size: ${theme.typography.fontSize.body1};
    line-height: ${theme.typography.lineHeight.body1};
  `,

  oldAmount: css`
    font-size: ${theme.typography.fontSize.h6};
    line-height: ${theme.typography.lineHeight.h6};
    text-decoration: line-through;

    ${media.desktop`
      font-size: ${theme.typography.fontSize.h5};
      line-height: ${theme.typography.lineHeight.h5};
    `}
  `,

  recurrence: css`
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.extrabold};
  `,

  freeAmountRecurrence: css``,

  specs: css`
    font-size: ${theme.typography.fontSize.body2};
    line-height: ${theme.typography.lineHeight.body2};
    font-weight: 700;
    margin: 0;
    text-transform: uppercase;

    ${media.desktop`
      font-size: ${theme.typography.fontSize.body1};
      line-height: ${theme.typography.lineHeight.body1};
    `}
  `,

  section: css``,

  detail: css`
    font-size: ${theme.typography.fontSize.body1};
    line-height: ${theme.typography.lineHeight.body1};
    margin: 0, 0, ${theme.spacing.sm};
  `,

  note: css`
    font-size: ${theme.typography.fontSize.caption};
    line-height: ${theme.typography.lineHeight.caption};
    margin: 0;

    ${media.desktop`
      font-size: ${theme.typography.fontSize.body3};
      line-height: ${theme.typography.lineHeight.body3};
    `}
  `,
};

/* used on sunrise */
const commercial = {
  ...common,
  recurrence: css`
    ${common.recurrence}
    font-size: ${theme.typography.fontSize.h5};
    line-height: ${theme.typography.lineHeight.h5};

    ${media.desktop`
      font-size: ${theme.typography.fontSize.display4};
      line-height: ${theme.typography.lineHeight.display4};
    `}
  `,
};

/* used on reshaping */
const details = {
  ...common,
  amount: css`
    ${common.amount}

    ${media.desktop`
      font-size: ${theme.typography.fontSize.display4};
      line-height: ${theme.typography.lineHeight.display4};
    `}
  `,

  recurrence: css`
    ${common.recurrence}
    font-size: ${theme.typography.fontSize.body2};
    line-height: ${theme.typography.lineHeight.body2};

    ${media.desktop`
      font-size: ${theme.typography.fontSize.h6};
    line-height: ${theme.typography.lineHeight.h6};
    `}
  `,

  freeAmountRecurrence: css`
    padding-left: 3px;
    font-size: ${theme.typography.fontSize.h3};
    font-weight: ${theme.typography.fontWeight.extrabold};
  `,

  detail: css`
    ${common.detail}
    font-size: ${theme.typography.fontSize.body3};
    line-height: ${theme.typography.lineHeight.body3};

    ${media.desktop`
      font-size: ${theme.typography.fontSize.body1};
      line-height: ${theme.typography.lineHeight.body1};
    `}

    p {
      margin: 0;
      margin-block: 0;
    }
  `,
};

// mva theme
const mvaCommon = {
  ...common,
  label: css`
    margin: 0;
    font-size: ${theme.typography.fontSize.body1};
    font-weight: ${theme.typography.fontWeight.bold};
  `,

  oldAmount: css`
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.light};
    font-size: ${theme.typography.fontSize.h6};
    line-height: ${theme.typography.lineHeight.h6};
    text-decoration: line-through;
  `,

  amount: css`
    font-family: ${theme.typography.fontFamily.primary};
    font-weight: ${theme.typography.fontWeight.extrabold};
    font-size: ${theme.typography.fontSize.h3};
    line-height: ${theme.typography.lineHeight.h3};

    ${media.desktop`
      ${pxToCssFont(44, 52)}
    `}
  `,

  recurrence: css`
    font-weight: 700;
    font-size: ${theme.typography.fontSize.body2};
    line-height: ${theme.typography.lineHeight.body2};

    ${media.tablet`
      font-size: ${theme.typography.fontSize.h6};
    line-height: ${theme.typography.lineHeight.h6};
    `}

    ${media.desktop`
      ${pxToCssFont(22, 32)}
    `}
  `,

  freeAmountRecurrence: css`
    font-weight: 700;
    padding-left: 3px;
    font-size: ${theme.typography.fontSize.h3};
    line-height: ${theme.typography.lineHeight.h3};

    ${media.desktop`
      ${pxToCssFont(44, 52)}
    `}
  `,

  detail: css`
    font-size: ${theme.typography.fontSize.body2};
    line-height: ${theme.typography.lineHeight.body2};
    margin: ${pxToRem(0, 0, 8)};

    ${media.tablet`
      font-size: ${theme.typography.fontSize.body1};
      line-height: ${theme.typography.lineHeight.body1};
    `}

    p {
      margin: 0;
      margin-block: 0;
    }
  `,
};

/* used on onboarding */
const mvaDetails = {
  ...mvaCommon,
};

const freeAmount = {
  ...common,
  amount: css`
    font-family: ${theme.typography.fontFamily.primary} !important;
    font-size: ${theme.typography.fontSize.h1};
    line-height: ${theme.typography.lineHeight.h1};
  `,
  recurrence: css`
    font-weight: ${theme.typography.fontWeight.regular} !important;
    font-family: ${theme.typography.fontFamily.primary} !important;
    font-size: ${theme.typography.fontSize.body1} !important;
    line-height: ${theme.typography.lineHeight.body1} !important;
  `,
  detail: css`
    font-weight: ${theme.typography.fontWeight.bold} !important;
    font-family: ${theme.typography.fontFamily.primary} !important;
    font-size: ${theme.typography.fontSize.body1} !important;
    line-height: ${theme.typography.lineHeight.body1} !important;
  `,
};
export const getVariant = (component: PriceVariantComponent, variant?: PriceVariant) => {
  switch (variant) {
    case 'details':
      return details[component];
    case 'mva:details':
      return mvaDetails[component];
    case 'freeAmount':
      return freeAmount[component];
    default:
      return commercial[component];
  }
};

export const Label = styled.h4<PriceVariantForStyled>`
  ${({ variant }) => getVariant('label', variant)}
`;

export const Specs = styled.p<PriceVariantForStyled>`
  ${({ variant }) => getVariant('specs', variant)}
  color: ${({ color }) =>
    color === 'white' ? theme.colors.text.disabled : theme.colors.text.secondary};
`;

export const Detail = styled.p<PriceVariantForStyled>`
  ${({ variant }) => getVariant('detail', variant)}
`;

export const Note = styled.p<PriceVariantForStyled>`
  ${({ variant }) => getVariant('note', variant)}
`;

export const Footer = styled.footer`
  display: flex;
  gap: ${pxToRem(20)};

  > button {
    flex-shrink: 0;
  }
`;

export const Section = styled.section<PriceVariantForStyled>`
  ${({ variant }) => getVariant('section', variant)}
  color: ${({ color }) => (color === 'white' ? theme.colors.text.inverse : '')};
  & > :last-child {
    margin: 0;
  }
`;

